/**
 * Asking `/shorten` for a short link (SPEC-TINYURL §5.1, §3.2).
 *
 * Same-origin, deliberately, exactly as `/prepare` is: no CORS, no preflight,
 * and `connect-src 'self'` covers it without naming an origin of ours.
 *
 * ## Sharing never hard-fails
 *
 * §3.2. Every failure path — offline, 5xx, rate-limited, a path the server will
 * not shorten — resolves to the **canonical long URL**, which always exists
 * because the browser built the state it describes. The worst outcome a visitor
 * can have is a longer link, never a modal that cannot answer.
 *
 * That is why this returns a discriminated result rather than throwing. A throw
 * would make "the service is down" a thing the caller has to remember to catch;
 * a `kind` makes it a branch the type system insists on.
 */

import { SITE_ORIGIN, type UrlState, buildUrl } from "../ui/url-state.js";

/** Same-origin, so this is a path and never an absolute URL. */
const SHORTEN_ENDPOINT = "/shorten";

/**
 * How long to wait before giving up and showing the long URL.
 *
 * A share modal is open and a person is looking at it. Four seconds is already
 * a long time to watch a spinner for something whose fallback is perfectly
 * usable — and the mint is one conditional write, so a slow answer means
 * something is wrong rather than something is big.
 */
const TIMEOUT_MS = 4000;

export type MintResult =
  | { readonly kind: "short"; readonly url: string; readonly hash: string }
  /**
   * The canonical long URL, with the reason recorded.
   *
   * `reason` is for the UI's wording and for nothing else — there is no
   * telemetry here — so a rate-limited visitor can be told something different
   * from an offline one if that ever proves worth doing.
   */
  | { readonly kind: "long"; readonly url: string; readonly reason: string };

/**
 * The payload hash CloudFront's OAC requires.
 *
 * The browser hashes its own body because the edge signs it: without this
 * header the signature covers a payload the origin never sees agreeing with,
 * and Lambda answers `The request signature we calculated does not match`.
 * `/prepare` documents the same requirement, and a hand-rolled `curl` against
 * either endpoint hits it immediately.
 */
async function bodyHash(body: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(body));
  return [...new Uint8Array(digest)]
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

/** `SITE_ORIGIN + buildUrl(state)` — the link that always works. */
export function longUrlFor(state: UrlState): string {
  return `${SITE_ORIGIN}${buildUrl(state)}`;
}

export async function mintLink(
  state: UrlState,
  fetchImpl: typeof fetch = fetch,
): Promise<MintResult> {
  const path = buildUrl(state);
  const long = `${SITE_ORIGIN}${path}`;

  // Nothing to shorten, and the server would say so too (§5.2.3). Answering
  // here saves a request whose outcome is already known.
  if (state.repoId === undefined) {
    return { kind: "long", url: long, reason: "no-view" };
  }

  const body = JSON.stringify({ path });
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);

  try {
    const response = await fetchImpl(SHORTEN_ENDPOINT, {
      method: "POST",
      headers: {
        "content-type": "application/json",
        "x-amz-content-sha256": await bodyHash(body),
      },
      body,
      // No credentials, ever. There are none to send, and asking for them would
      // make this a request that *could* carry identity.
      credentials: "omit",
      signal: controller.signal,
    });

    if (!response.ok) {
      return { kind: "long", url: long, reason: `http-${response.status}` };
    }

    const payload: unknown = await response.json();
    const shortUrl = (payload as { shortUrl?: unknown })?.shortUrl;
    const hash = (payload as { hash?: unknown })?.hash;
    if (typeof shortUrl !== "string" || typeof hash !== "string") {
      return { kind: "long", url: long, reason: "malformed-response" };
    }

    /*
     * The §2.1 property, checked where it is *used* rather than only where it
     * is stored.
     *
     * The server cannot mint an off-origin link — creation canonicalizes and
     * `buildUrl` constructs its output — so this can only fire if something
     * between here and there is not our endpoint. That is exactly the case
     * worth refusing: a link this page is about to hand someone to paste
     * elsewhere is the last place to take a URL on trust.
     */
    if (!shortUrl.startsWith(`${SITE_ORIGIN}/`)) {
      return { kind: "long", url: long, reason: "off-origin-response" };
    }

    return { kind: "short", url: shortUrl, hash };
  } catch (error) {
    const aborted = error instanceof DOMException && error.name === "AbortError";
    return { kind: "long", url: long, reason: aborted ? "timeout" : "network" };
  } finally {
    clearTimeout(timer);
  }
}
